import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { advantages } from "@/mocks/advantages";
import { testimonials } from "@/mocks/testimonials";
import JsonLd from "@/components/base/JsonLd";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://wyzon.io";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Wyzon",
  "url": siteUrl,
  "description": "WhatsApp automation and customer messaging platform for businesses. Automate messaging, manage chats, send bulk campaigns, and build product catalogs.",
  "sameAs": [],
};

function HeroSection() {
  const { t } = useTranslation("home");

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-background-50">
      {/* Dot pattern grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1.5px 1.5px, oklch(var(--foreground-50)) 1.5px, transparent 0)",
          backgroundSize: "48px 48px",
        }}
      ></div>

      {/* Dark top strip for navbar visibility */}
      <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-background-950/80 via-background-950/30 to-transparent pointer-events-none z-[1]"></div>

      {/* Gradient orbs */}
      <div className="absolute top-1/4 right-[15%] w-[500px] h-[500px] rounded-full bg-accent-500/8 blur-[150px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 left-[8%] w-[400px] h-[400px] rounded-full bg-primary-500/6 blur-[120px] pointer-events-none"></div>

      <div className="relative z-10 w-full py-24 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16 xl:gap-20">
            {/* ===== LEFT: Text Content ===== */}
            <div className="flex-1 text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-accent-100 border border-accent-200/50 mb-8">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-accent-600"></span>
                </span>
                <span className="text-accent-600 text-xs font-semibold tracking-wide uppercase">
                  {t("hero.badge")}
                </span>
              </div>

              {/* Heading */}
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground-950 leading-[1.08] mb-6 tracking-tight">
                {t("hero.title.line1")}
                <br />
                <span className="text-accent-500">{t("hero.title.line2")}</span>
              </h1>

              {/* Subtitle */}
              <p className="text-foreground-600 text-base md:text-lg max-w-md mx-auto lg:mx-0 mb-10 leading-relaxed">
                {t("hero.subtitle")}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3">
                <Link
                  to="/dashboard"
                  className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-7 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                >
                  {t("hero.cta.getStarted")}
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <Link
                  to="/plans"
                  className="text-foreground-600 hover:text-foreground-900 text-sm font-medium px-7 py-3 rounded-full border border-foreground-200 hover:border-foreground-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  {t("hero.cta.viewPlans")}
                </Link>
              </div>
            </div>

            {/* ===== RIGHT: Interactive Dashboard Mockup ===== */}
            <div className="flex-1 w-full max-w-[520px] lg:max-w-none">
              <div className="relative">
                {/* Floating stat card — top right */}
                <div className="absolute -top-5 -right-3 sm:-top-7 sm:-right-5 z-20 animate-[float_6s_ease-in-out_infinite] hidden sm:block">
                  <div className="bg-white/85 backdrop-blur-xl border border-background-200/70 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-message-3-line text-accent-600 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">
                          {t("hero.stat.messages")}
                        </p>
                        <p className="text-foreground-950 font-bold text-base">12,847</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating stat card — bottom left */}
                <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 z-20 animate-[float_7s_ease-in-out_infinite_1s] hidden sm:block">
                  <div className="bg-white/85 backdrop-blur-xl border border-background-200/70 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-pulse-line text-primary-600 text-sm"></i>
                      </div>
                      <div>
                        <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">
                          {t("hero.stat.responseRate")}
                        </p>
                        <p className="text-foreground-950 font-bold text-base">98.7%</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main chat mockup card */}
                <div className="relative bg-white/90 backdrop-blur-xl border border-background-200/70 rounded-2xl overflow-hidden">
                  {/* Chat header */}
                  <div className="flex items-center gap-3 px-5 py-3.5 border-b border-background-200/60 bg-background-50">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 text-xs font-bold flex-shrink-0">
                      SJ
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground-950 text-sm font-semibold truncate">
                        {t("hero.chat.senderName")}
                      </p>
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent-500"></div>
                        <p className="text-accent-600 text-[11px]">{t("hero.chat.online")}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-search-line text-foreground-300 text-sm cursor-pointer hover:text-foreground-500 transition-colors"></i>
                      <i className="ri-more-2-fill text-foreground-300 text-sm cursor-pointer hover:text-foreground-500 transition-colors"></i>
                    </div>
                  </div>

                  {/* Messages area */}
                  <div className="px-5 py-5 space-y-4 min-h-[270px]">
                    {/* Received message */}
                    <div className="flex gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-background-200 flex-shrink-0 mt-0.5 flex items-center justify-center text-[10px] font-bold text-foreground-500">
                        SJ
                      </div>
                      <div>
                        <div className="bg-background-100 rounded-2xl rounded-tl-md px-4 py-2.5 max-w-[240px]">
                          <p className="text-foreground-700 text-[13px] leading-relaxed">
                            {t("hero.chat.msg1")}
                          </p>
                        </div>
                        <span className="text-foreground-400 text-[10px] mt-1 inline-block">
                          10:24 AM
                        </span>
                      </div>
                    </div>

                    {/* Sent message */}
                    <div className="flex justify-end">
                      <div>
                        <div className="bg-accent-100 rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[240px]">
                          <p className="text-accent-700 text-[13px] leading-relaxed">
                            {t("hero.chat.msg2")}
                          </p>
                        </div>
                        <span className="text-foreground-400 text-[10px] mt-1 inline-block w-full text-right">
                          10:24 AM
                        </span>
                      </div>
                    </div>

                    {/* Received message 2 */}
                    <div className="flex gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-background-200 flex-shrink-0 mt-0.5 flex items-center justify-center text-[10px] font-bold text-foreground-500">
                        SJ
                      </div>
                      <div>
                        <div className="bg-background-100 rounded-2xl rounded-tl-md px-4 py-2.5 max-w-[240px]">
                          <p className="text-foreground-700 text-[13px] leading-relaxed">
                            {t("hero.chat.msg3")}
                          </p>
                        </div>
                        <span className="text-foreground-400 text-[10px] mt-1 inline-block">
                          10:25 AM
                        </span>
                      </div>
                    </div>

                    {/* Typing indicator */}
                    <div className="flex gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-background-200 flex-shrink-0 mt-0.5 flex items-center justify-center text-[10px] font-bold text-foreground-500">
                        SJ
                      </div>
                      <div className="bg-background-100 rounded-2xl rounded-tl-md px-4 py-3 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce"></span>
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: "0.15s" }}></span>
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: "0.3s" }}></span>
                      </div>
                    </div>
                  </div>

                  {/* Chat input */}
                  <div className="px-5 py-3.5 border-t border-background-200/60 bg-transparent">
                    <div className="flex items-center gap-3 bg-background-100 rounded-xl px-4 py-2.5 border border-background-200/50">
                      <i className="ri-emotion-line text-foreground-400 text-sm"></i>
                      <span className="text-foreground-400 text-[13px] flex-1">
                        {t("hero.chat.placeholder")}
                      </span>
                      <i className="ri-attachment-2 text-foreground-400 text-sm cursor-pointer hover:text-foreground-600 transition-colors"></i>
                      <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center cursor-pointer hover:bg-accent-200 transition-colors flex-shrink-0">
                        <i className="ri-send-plane-fill text-accent-600 text-xs"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
        <button
          onClick={() =>
            document
              .getElementById("advantages")
              ?.scrollIntoView({ behavior: "smooth" })
          }
          className="cursor-pointer"
          aria-label={t("hero.scrollLabel")}
        >
          <i className="ri-arrow-down-line text-foreground-300 text-xl hover:text-foreground-500 transition-colors"></i>
        </button>
      </div>

      {/* Keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-14px); }
        }
      `}</style>
    </section>
  );
}

function AdvantagesSection() {
  const { t } = useTranslation("home");
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const cards = cardsRef.current?.querySelectorAll(".advantage-card");
    cards?.forEach((card, i) => {
      (card as HTMLElement).style.opacity = "0";
      (card as HTMLElement).style.transform = "translateY(30px)";
      (card as HTMLElement).style.transition = `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`;
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="advantages" ref={sectionRef} className="py-20 md:py-28 lg:py-32 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-16 md:mb-20">
          <span className="inline-block text-primary-600 text-xs font-medium uppercase tracking-widest mb-4">
            {t("advantages.heading.badge")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-4">
            {t("advantages.heading.title")}
          </h2>
          <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {t("advantages.heading.subtitle")}
          </p>
        </div>

        <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {advantages.map((adv, i) => (
            <div
              key={adv.id}
              className="advantage-card group relative bg-background-100 rounded-2xl overflow-hidden p-6 md:p-8 hover:shadow-lg transition-all duration-500 cursor-default border border-background-200/50"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary-100 flex items-center justify-center mb-5 group-hover:bg-primary-500 transition-colors duration-300">
                <i className={`${adv.icon} text-primary-600 text-lg md:text-xl group-hover:text-white transition-colors duration-300`}></i>
              </div>
              <h3 className="font-heading text-xl md:text-2xl text-foreground-950 mb-3">
                {t(`advantages.item.${i}.title`)}
              </h3>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                {t(`advantages.item.${i}.description`)}
              </p>
              <div className="mt-5 inline-flex items-center gap-1.5 text-primary-600 text-sm font-medium group-hover:text-primary-700 transition-colors">
                <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-50 text-primary-700">
                  {t(`advantages.item.${i}.category`)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  const { t } = useTranslation("home");
  const sectionRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const cards = cardsRef.current?.querySelectorAll(".testimonial-card");
    cards?.forEach((card, i) => {
      (card as HTMLElement).style.opacity = "0";
      (card as HTMLElement).style.transform = "translateY(30px)";
      (card as HTMLElement).style.transition = `opacity 0.6s ease ${i * 0.08}s, transform 0.6s ease ${i * 0.08}s`;
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const col1 = testimonials.filter((_, i) => i % 3 === 0);
  const col2 = testimonials.filter((_, i) => i % 3 === 1);
  const col3 = testimonials.filter((_, i) => i % 3 === 2);

  return (
    <section id="testimonials" ref={sectionRef} className="py-20 md:py-28 lg:py-32 bg-background-100">
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
        <span className="inline-block text-accent-600 text-xs font-medium uppercase tracking-widest mb-4">
          {t("testimonials.badge")}
        </span>
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-3">
          {t("testimonials.title.line1")}
        </h2>
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-16 md:mb-20">
          {t("testimonials.title.line2")}
        </h2>

        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          <div className="flex flex-col gap-5 md:gap-6">
            {col1.map((testimonial, i) => (
              <div
                key={testimonial.id}
                className="testimonial-card bg-background-50 rounded-2xl p-6 md:p-7 border border-background-200/50"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${testimonial.avatarColor}`}>
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="text-foreground-950 text-sm font-semibold">{t(`testimonials.item.${i * 3}.name`)}</p>
                    <p className="text-foreground-500 text-xs">{t(`testimonials.item.${i * 3}.role`)}</p>
                  </div>
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed italic">
                  &ldquo;{t(`testimonials.item.${i * 3}.quote`)}&rdquo;
                </p>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-5 md:gap-6 md:mt-8">
            {col2.map((testimonial, i) => (
              <div
                key={testimonial.id}
                className="testimonial-card bg-background-50 rounded-2xl p-6 md:p-7 border border-background-200/50"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${testimonial.avatarColor}`}>
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="text-foreground-950 text-sm font-semibold">{t(`testimonials.item.${i * 3 + 1}.name`)}</p>
                    <p className="text-foreground-500 text-xs">{t(`testimonials.item.${i * 3 + 1}.role`)}</p>
                  </div>
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed italic">
                  &ldquo;{t(`testimonials.item.${i * 3 + 1}.quote`)}&rdquo;
                </p>
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-5 md:gap-6 md:mt-4">
            {col3.map((testimonial, i) => (
              <div
                key={testimonial.id}
                className="testimonial-card bg-background-50 rounded-2xl p-6 md:p-7 border border-background-200/50"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${testimonial.avatarColor}`}>
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="text-foreground-950 text-sm font-semibold">{t(`testimonials.item.${i * 3 + 2}.name`)}</p>
                    <p className="text-foreground-500 text-xs">{t(`testimonials.item.${i * 3 + 2}.role`)}</p>
                  </div>
                </div>
                <p className="text-foreground-700 text-sm leading-relaxed italic">
                  &ldquo;{t(`testimonials.item.${i * 3 + 2}.quote`)}&rdquo;
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { t } = useTranslation("home");

  return (
    <section id="contact-highlight" className="py-20 md:py-28 lg:py-32 bg-background-50">
      <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 text-center">
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 mb-4">
          {t("cta.title")}
        </h2>
        <p className="text-foreground-500 text-base md:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
          {t("cta.subtitle")}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/plans"
            className="bg-foreground-950 text-white text-sm font-medium px-8 py-3.5 rounded-full hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            {t("cta.viewPlans")}
            <i className="ri-arrow-right-line text-sm"></i>
          </Link>
          <button
            onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
            className="text-foreground-700 text-sm font-medium px-8 py-3.5 rounded-full border border-background-300 hover:border-foreground-300 hover:text-foreground-950 transition-all cursor-pointer whitespace-nowrap"
          >
            {t("cta.talkToSales")}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="relative">
      <JsonLd data={organizationSchema} />
      <style>{`
        .animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
      `}</style>
      <HeroSection />
      <AdvantagesSection />
      <TestimonialsSection />
      <CTASection />
    </div>
  );
}